import type { ReactNode } from 'react';

export interface TimelineItem {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  time?: string;
  status: 'completed' | 'current' | 'pending' | 'rejected';
  icon?: string;
  extra?: ReactNode;
}

interface TimelineProps {
  items: TimelineItem[];
  className?: string;
  /** When true, use left-aligned single-column layout (no card borders) */
  leftAlign?: boolean;
}

// Dot style per status – used in left-align mode
const dotMeta: Record<
  TimelineItem['status'],
  { outerRing: string; innerDot: string; labelColor: string; label: string; lineColor: string }
> = {
  completed: {
    outerRing: 'border-2 border-accent-500',
    innerDot: 'bg-accent-500',
    labelColor: 'text-foreground-900',
    label: '已完成',
    lineColor: 'bg-accent-300',
  },
  current: {
    outerRing: 'border-2 border-primary-500',
    innerDot: 'bg-primary-500 animate-pulse',
    labelColor: 'text-foreground-900',
    label: '进行中',
    lineColor: 'bg-background-300',
  },
  pending: {
    outerRing: 'border-2 border-background-300',
    innerDot: 'bg-background-300',
    labelColor: 'text-foreground-400',
    label: '待处理',
    lineColor: 'bg-background-200',
  },
  rejected: {
    outerRing: 'border-2 border-primary-500',
    innerDot: 'bg-primary-500',
    labelColor: 'text-foreground-900',
    label: '已驳回',
    lineColor: 'bg-background-200',
  },
};

// Marker style for the center/alternating layout
const statusMeta: Record<
  TimelineItem['status'],
  { markerBg: string; markerRing: string; iconClass: string; textColor: string; label: string }
> = {
  completed: {
    markerBg: 'bg-accent-500',
    markerRing: '',
    iconClass: 'ri-check-line text-background-50',
    textColor: 'text-accent-600',
    label: '已完成',
  },
  current: {
    markerBg: 'bg-primary-500',
    markerRing: 'ring-4 ring-primary-100',
    iconClass: 'ri-loader-4-line animate-spin text-background-50',
    textColor: 'text-primary-600',
    label: '进行中',
  },
  pending: {
    markerBg: 'bg-background-50 border-2 border-background-300',
    markerRing: '',
    iconClass: '',
    textColor: 'text-foreground-400',
    label: '待处理',
  },
  rejected: {
    markerBg: 'bg-primary-500',
    markerRing: '',
    iconClass: 'ri-close-line text-background-50',
    textColor: 'text-primary-600',
    label: '已驳回',
  },
};

function LeftAlignTimeline({ items, className = '' }: { items: TimelineItem[]; className?: string }) {
  return (
    <div className={`relative pl-2 ${className}`}>
      {items.map((item, index) => {
        const meta = dotMeta[item.status];
        const isLast = index === items.length - 1;

        return (
          <div key={item.id} className="relative flex items-start gap-4">
            {/* Dot + vertical connector */}
            <div className="relative flex flex-col items-center shrink-0" style={{ width: 18 }}>
              {/* Dot */}
              <div
                className={`relative z-10 w-[18px] h-[18px] rounded-full flex items-center justify-center bg-white shrink-0 ${meta.outerRing}`}
              >
                <div className={`w-[7px] h-[7px] rounded-full ${meta.innerDot}`} />
              </div>
              {/* Connector line below dot */}
              {!isLast && (
                <div className={`w-px flex-1 min-h-[32px] mt-0.5 ${meta.lineColor}`} />
              )}
            </div>

            {/* Content */}
            <div className={`flex-1 pb-5 ${isLast ? 'pb-0' : ''}`}>
              {/* Header row */}
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-0.5">
                <span
                  className={`text-sm font-semibold leading-[18px] ${
                    item.status === 'pending' ? 'text-foreground-400' : 'text-foreground-900'
                  }`}
                >
                  {item.title}
                </span>
                {item.subtitle && (
                  <span className="text-xs text-foreground-400 leading-[18px]">{item.subtitle}</span>
                )}
                <span
                  className={`text-xs leading-[18px] ${
                    item.status === 'completed'
                      ? 'text-accent-500'
                      : item.status === 'current'
                      ? 'text-primary-500'
                      : item.status === 'rejected'
                      ? 'text-primary-500'
                      : 'text-foreground-400'
                  }`}
                >
                  {item.status === 'current' && <i className="ri-loader-4-line animate-spin mr-0.5" />}
                  {item.status === 'completed' && <i className="ri-check-line mr-0.5" />}
                  {item.status === 'rejected' && <i className="ri-close-line mr-0.5" />}
                  {meta.label}
                </span>
              </div>

              {/* Time */}
              {item.time && (
                <p className="text-xs text-foreground-400 mb-1">{item.time}</p>
              )}

              {/* Description – no border, just subtle tint */}
              {item.description && (
                <div className="mt-1.5 text-sm text-foreground-500 leading-relaxed">
                  <i className="ri-chat-quote-line text-foreground-300 mr-1" />
                  {item.description}
                </div>
              )}

              {item.extra && <div className="mt-2">{item.extra}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Timeline({ items, className = '', leftAlign = false }: TimelineProps) {
  if (items.length === 0) return null;

  if (leftAlign) {
    return <LeftAlignTimeline items={items} className={className} />;
  }

  // ---- Original center alternating layout ----
  return (
    <div className={`relative ${className}`}>
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden md:block" />
      <div className="absolute left-[19px] top-3 bottom-3 w-px bg-background-200 md:hidden" />

      <div className="space-y-0">
        {items.map((item, index) => {
          const meta = statusMeta[item.status];
          const isEven = index % 2 === 0;

          const contentPanel = (
            <div
              className={`bg-white rounded-xl border border-background-200 p-4 md:p-5 ${
                item.status === 'current' ? 'ring-1 ring-primary-200' : ''
              }`}
            >
              <div className="flex items-center flex-wrap gap-x-2.5 gap-y-1 mb-1">
                <span
                  className={`text-sm font-semibold ${
                    item.status === 'pending' ? 'text-foreground-500' : 'text-foreground-900'
                  }`}
                >
                  {item.title}
                </span>
                {item.subtitle && <span className="text-xs text-foreground-400">{item.subtitle}</span>}
                <span className={`inline-flex items-center gap-1 text-xs font-medium ${meta.textColor}`}>
                  {item.status === 'current' && <i className="ri-loader-4-line animate-spin" />}
                  {item.status === 'completed' && <i className="ri-check-line" />}
                  {item.status === 'rejected' && <i className="ri-close-line" />}
                  {meta.label}
                </span>
              </div>
              {item.time && <p className="text-xs text-foreground-400 mb-1.5">{item.time}</p>}
              {item.description && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-background-50 text-sm text-foreground-600 leading-relaxed">
                  <i className="ri-chat-quote-line text-foreground-300 mr-1.5" />
                  {item.description}
                </div>
              )}
              {item.extra && <div className="mt-2">{item.extra}</div>}
            </div>
          );

          return (
            <div key={item.id} className="relative flex flex-col md:flex-row md:items-start">
              <div className="hidden md:flex w-1/2 pr-10 justify-end">{isEven ? contentPanel : null}</div>
              <div className="relative z-10 flex items-center md:justify-center py-2 md:py-4">
                <div
                  className={`relative w-[18px] h-[18px] md:w-5 md:h-5 rounded-full flex items-center justify-center shrink-0 ${meta.markerBg} ${meta.markerRing}`}
                >
                  {meta.iconClass ? (
                    <i className={`${meta.iconClass} text-[10px] md:text-[11px]`} />
                  ) : item.icon ? (
                    <i className={`${item.icon} text-[10px] md:text-[11px] text-foreground-400`} />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground-400" />
                  )}
                </div>
              </div>
              <div className="hidden md:flex w-1/2 pl-10">{!isEven ? contentPanel : null}</div>
              <div className="md:hidden pl-10 pb-4">{contentPanel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}